///
/// EarthBrowser
///
/// Created by Matt Giger on 1/24/2026
/// Copyright (c) 2026$ Geodata Labs
///

import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { geographic, wgs } from "@/earthbrowser/utils/geographic"
import { NodeBase } from "@stovokor/engine/NodeBase"
import { type RenderActionSet, Renderer } from "@stovokor/engine/Renderer"
import { type RenderState } from "@stovokor/engine/RenderState"
import { type matrix } from "@stovokor/math/matrix"
import { rgba, type color } from "@stovokor/math/color"
import { type Material } from "@stovokor/shader/Material"
import { ShaderSourceDefinition } from "@stovokor/shader/ShaderSourceCode"
import { type Texture } from "@stovokor/shader/Texture"
import { UniformDynamic } from "@stovokor/shader/Uniform"
import { PressureIsolineSphereGeometry } from "./PressureIsolineSphereGeometry"
import { type PressureFieldTextureFrame } from "./PressureFieldTextureFrame"
import { PressureFieldTextureSeries } from "./PressureFieldTextureSeries"

/**
 * How a scalar field is contoured by pressure_isolines_sphere.glsl. The
 * same shader and node draw MSLP isobars (hPa) and 500 hPa height
 * contours (dam); only these parameters differ.
 */
export interface IsolineStyle {
	/** Texels below this are invalid (fields mark invalid cells with 0) */
	validMin: number
	/** Contour interval ladder: intervalBase * 2^k clamped to [min, max] */
	intervalBase: number
	intervalMin: number
	intervalMax: number
	/** Ideal interval per source texel spanned by one screen pixel */
	intervalPerTexel: number
	/** Tint fill (when enabled) is neutral at tintCenter, saturated tintScale away */
	tintCenter: number
	tintScale: number
	minorColor: color
	majorColor: color
}

/** MSLP in hPa on the 0.25 deg grid: 1/2/4/8 hPa ladder, light blue-white */
export const cIsobarStyle: IsolineStyle = {
	validMin: 500,
	intervalBase: 1,
	intervalMin: 1,
	intervalMax: 8,
	intervalPerTexel: 5,
	tintCenter: 1013.25,
	tintScale: 40,
	// Light cores of the cased lines (casing in pressure_isolines_sphere.glsl)
	minorColor: rgba(0.75, 0.82, 0.95, 0.4),
	majorColor: rgba(0.95, 0.97, 1.0, 0.6),
}

/** 500 hPa geopotential height in dam on the 0.5 deg grid: 3/6/12/24 dam
 *  ladder (standard charts use 6), warm gold so the family is distinct
 *  from the isobars below */
export const cHeightContourStyle: IsolineStyle = {
	validMin: 100,
	intervalBase: 3,
	intervalMin: 3,
	intervalMax: 24,
	// Half the texel density of the MSLP grid, so twice the per-texel interval
	intervalPerTexel: 10,
	tintCenter: 552,
	tintScale: 30,
	minorColor: rgba(0.98, 0.85, 0.5, 0.5),
	majorColor: rgba(1.0, 0.93, 0.68, 0.72),
}

class PressureIsolineSphereMaterial implements Material {
	public available: boolean = true
	public transparency: boolean = true
}

class PressureIsolineSphereRenderer extends Renderer {
	private modelViewProjection = new UniformDynamic<matrix>()
	private sphereRadius = new UniformDynamic<number>()
	private texture0 = new UniformDynamic<Texture>()
	private texture1 = new UniformDynamic<Texture>()
	private flowTexture = new UniformDynamic<Texture>()
	private blendFactor = new UniformDynamic<number>()
	private fadeFactor = new UniformDynamic<number>()
	private tintOpacity = new UniformDynamic<number>()

	private minorColor = new UniformDynamic<color>()
	private majorColor = new UniformDynamic<color>()
	private validMin = new UniformDynamic<number>()
	private intervalBase = new UniformDynamic<number>()
	private intervalMin = new UniformDynamic<number>()
	private intervalMax = new UniformDynamic<number>()
	private intervalPerTexel = new UniformDynamic<number>()
	private tintCenter = new UniformDynamic<number>()
	private tintScale = new UniformDynamic<number>()

	/** When set, the shell radius follows the camera altitude each frame */
	public altitudeForCamera?: (cameraAltitudeKm: number) => number
	/** Shell altitude used for the last frame (km above the ellipsoid) */
	public currentAltitudeKm: number

	constructor(context: EarthBrowserContext, geometry: PressureIsolineSphereGeometry, material: Material, style: IsolineStyle) {
		const source = new ShaderSourceDefinition("pressure_isolines_sphere")
		super(context, source, geometry, [material])

		this.drawState.cullFace = false
		this.drawState.depthWrite = false

		this.sphereRadius.value = geometry.sphereRadius
		this.currentAltitudeKm = geometry.sphereRadius - wgs.a
		this.blendFactor.value = 0
		this.fadeFactor.value = 1
		this.tintOpacity.value = 0

		this.minorColor.value = style.minorColor
		this.majorColor.value = style.majorColor
		this.validMin.value = style.validMin
		this.intervalBase.value = style.intervalBase
		this.intervalMin.value = style.intervalMin
		this.intervalMax.value = style.intervalMax
		this.intervalPerTexel.value = style.intervalPerTexel
		this.tintCenter.value = style.tintCenter
		this.tintScale.value = style.tintScale
	}

	public updateTextures(tex0: Texture, tex1: Texture, flow: Texture, blend: number, fade: number): void {
		this.texture0.value = tex0
		this.texture1.value = tex1
		this.flowTexture.value = flow
		this.blendFactor.value = blend
		this.fadeFactor.value = fade
	}

	public setTintOpacity(value: number): void {
		this.tintOpacity.value = value
	}

	protected stateUpdate(state: RenderState): void {
		this.modelViewProjection.value = state.modelViewProjection
		if (this.altitudeForCamera) {
			const cameraAltitudeKm = new geographic(state.camera.position).altitude
			this.currentAltitudeKm = this.altitudeForCamera(cameraAltitudeKm)
			this.sphereRadius.value = wgs.a + this.currentAltitudeKm
		}
	}
}

export interface PressureIsolineSphereOptions {
	/** H3 resolution of the shell mesh */
	resolution?: number
	style?: IsolineStyle
	/** Fixed shell altitude (km); default is the 5 km surface analysis shell */
	altitudeKm?: number
	/**
	 * Camera-dependent shell altitude (km). When given, altitudeKm sizes the
	 * culling bounds and should be the maximum this returns.
	 */
	altitudeForCamera?: (cameraAltitudeKm: number) => number
}

export class PressureIsolineSphereNode extends NodeBase {
	public context: EarthBrowserContext
	private geometry: PressureIsolineSphereGeometry
	private material: PressureIsolineSphereMaterial
	private sphereRenderer: PressureIsolineSphereRenderer
	private textureSeries: PressureFieldTextureSeries

	constructor(context: EarthBrowserContext, options: PressureIsolineSphereOptions = {}) {
		super()
		this.context = context

		this.geometry = new PressureIsolineSphereGeometry(context, options.resolution ?? 1, options.altitudeKm)
		this.material = new PressureIsolineSphereMaterial()
		this.sphereRenderer = new PressureIsolineSphereRenderer(context, this.geometry, this.material, options.style ?? cIsobarStyle)
		this.sphereRenderer.altitudeForCamera = options.altitudeForCamera
		this.renderer = this.sphereRenderer

		this.textureSeries = new PressureFieldTextureSeries(context)
	}

	/** Shell altitude in km for the last rendered frame */
	public get altitudeKm(): number {
		return this.sphereRenderer.currentAltitudeKm
	}

	public setFrames(frames: PressureFieldTextureFrame[]): void {
		this.textureSeries.setFrames(frames)
	}

	public setPressureTint(enabled: boolean): void {
		this.sphereRenderer.setTintOpacity(enabled ? 0.35 : 0)
	}

	public clearFrames(): void {
		this.textureSeries.clear()
	}

	/**
	 * Free all GPU field/flow textures while keeping frame metadata, so a
	 * hidden synoptic layer reclaims memory; frames re-compute/re-fetch when
	 * the layer is shown again.
	 */
	public purgeTextures(): void {
		this.textureSeries.purgeAll()
	}

	override renderActions(state: RenderState): RenderActionSet[] {
		const series = this.textureSeries
		series.updateState(state)

		// Advect only when blending toward a genuinely different next frame;
		// when texture1 falls back to texture0 (next not loaded yet) a warp
		// would double-image the field, so use zero flow instead
		const flowTexture = series.currentFrame?.flowTexture
		const flow = (flowTexture?.available && series.texture1 !== series.texture0)
			? flowTexture
			: this.context.blankTexture

		this.sphereRenderer.updateTextures(
			series.texture0,
			series.texture1,
			flow,
			series.blendFactor,
			series.fadeFactor
		)

		return super.renderActions(state)
	}

	override destroy(): void {
		this.textureSeries.clear()
		this.geometry.destroy()
		super.destroy()
	}
}
